import { Flex, Input, InputGroup, InputRightElement } from '@chakra-ui/react'
import React, { useEffect, useRef } from 'react'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { SearchValue } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'
import { tryGetValueOrDefault } from '~/utils/common'

import { useSearchInputString } from './atoms/SearchInput.atom'
import {
  PhoneSearchInputVariants,
  usePhoneSearchInputStyles,
} from './componentStyles/PhoneSearchInput.style'

interface SearchInputProps {
  searchInputValue?: string
  onSearchChange?: (newSearchString: SearchValue) => void
  disabled?: boolean
}
export const PhoneSearchInput = ({
  searchInputValue: externalInputValue,
  onSearchChange,
  disabled,
}: SearchInputProps) => {
  const { searchValue, setInputString, getFormattedInput } =
    useSearchInputString()
  const inputRef = useRef<HTMLInputElement>(null)
  const keypress = useKeyPresses()

  const { label: inputString } = searchValue

  const derivedInputValue = tryGetValueOrDefault(
    externalInputValue,
    inputString,
  )

  //when any key is pressed, focus the input box
  useEffect(() => {
    if (disabled) {
      return
    }

    inputRef.current?.focus()
  }, [keypress])

  const handleSearchChange = (newSearchString: string) => {
    //if externally controlled, don't set the internal input value, just hand back the formatting change
    if (externalInputValue !== undefined) {
      const { clean, formatted: derived } = getFormattedInput(newSearchString)
      onSearchChange?.(SearchValue.fromValueAndLabel(clean, derived))
      return
    }

    const searchValue = setInputString(newSearchString)
    onSearchChange?.(searchValue)
  }

  const handleClearSearch = () => {
    handleSearchChange('')
  }

  const variant = disabled
    ? PhoneSearchInputVariants.Disabled
    : derivedInputValue
      ? PhoneSearchInputVariants.Filled
      : PhoneSearchInputVariants.Ready

  const styles = usePhoneSearchInputStyles({ variant })

  return (
    <Flex sx={styles.container}>
      <InputGroup>
        <Input
          ref={inputRef}
          disabled={disabled}
          sx={styles.input}
          placeholder="Enter Name or Number"
          variant="unstyled"
          value={derivedInputValue}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <InputRightElement sx={styles.inputRightElement}>
          <MangoButton
            onClick={handleClearSearch}
            variant={MangoButtonVariant.Default_100}
            size={Size.sm}
          >
            Clear
          </MangoButton>
        </InputRightElement>
      </InputGroup>
    </Flex>
  )
}
